/**
 * downloadZip — Client-side ZIP builder for photo galleries.
 *
 * Fetches each photo in parallel (concurrency 4 by default), streams
 * the bytes into JSZip, then triggers a browser download of the
 * generated blob. Runs entirely client-side — no edge function
 * needed. JSZip is dynamic-imported so its ~50KB chunk only ships
 * when a client clicks "Download all" or "Download as ZIP".
 *
 * Fetches use `originalUrl()` from imageTransform so the ZIP holds
 * untouched originals, not the 400/1200-wide preview variants that
 * the grid and lightbox render.
 *
 * A failed fetch on any single photo does NOT abort the whole ZIP —
 * that photo is skipped and reported back in the return value so the
 * caller can toast "Downloaded 38 of 40. 2 failed."
 *
 * HEIC (iPhone) images are converted to JPEG in-flight via a lazy
 * heic2any import, mirroring the single-photo download path so the
 * ZIP contains browser-openable files.
 *
 * P1-2 — BOUNDED MEMORY via part rotation. The old build held every
 * photo blob in one JSZip until the very end: a 500-photo full-res
 * wedding gallery is easily 2–4 GB of live blobs plus a same-sized
 * output blob at generateAsync time, which OOM-kills a phone
 * browser's tab. JSZip has no true streaming sink (generateAsync
 * materializes the archive), so the least-invasive correct fix is to
 * CHUNK: entries accumulate into the current part and, when the next
 * entry would push it past PART_LIMIT_BYTES, that part is finalized
 * and handed to the browser as `<name>-partN.zip` before building
 * continues. Peak memory ≈ one part of input blobs + one part of
 * output + `concurrency` in-flight fetches, regardless of gallery
 * size. Galleries that fit in one part are byte-for-byte the old
 * behavior with the old filename. (Browsers may ask permission for
 * multiple automatic downloads on the first multi-part run — callers
 * surface that via the `parts` count in the result.)
 *
 * P1-2 also closes the DEMO-2 silence: HEIC-conversion and
 * watermark-bake fallthroughs were console-only. They still degrade
 * gracefully (original HEIC bytes / unmarked bytes get zipped), but
 * the counts now come back in ZipResult so callers can say so.
 */
import { originalUrl } from './imageTransform';
import { saveBlob } from '@/lib/saveBlob';
import type { WatermarkConfig } from '@/components/watermark/WatermarkOverlay';

export interface ZipPhoto {
  id: string;
  file_url: string;
  name: string;
}

export type ZipPhase = 'downloading' | 'zipping' | 'done';

export interface ZipProgress {
  current: number;
  total: number;
  phase: ZipPhase;
}

export interface ZipResult {
  succeeded: number;
  failed: number;
  /** P1-2 — how many ZIP files were handed to the browser. 1 for
   *  galleries under the part limit (the old behavior); >1 means the
   *  download was split into `-partN.zip` files and the caller should
   *  tell the user to expect multiple files. 0 when nothing succeeded
   *  (no empty archive is saved). */
  parts: number;
  /** P1-2 / DEMO-2 — HEIC files whose JPEG conversion failed and whose
   *  ORIGINAL bytes were zipped instead (may not open outside Apple
   *  devices). Previously console-only. */
  heicFallbacks: number;
  /** P1-2 / DEMO-2 — photos whose watermark bake failed and whose
   *  UNMARKED bytes were zipped instead. Previously console-only. */
  watermarkFallbacks: number;
}

export interface DownloadZipOptions {
  photos: ZipPhoto[];
  /** Output filename. Defaults to 'photos.zip'. */
  filename?: string;
  /** Parallel fetch concurrency. Defaults to 6 (GAL-7 — photos come
   *  off the public CDN, so a wider fan-out is nearly free). */
  concurrency?: number;
  /** Progress callback fired as fetches complete + when zipping starts. */
  onProgress?: (progress: ZipProgress) => void;
  /** Wave 6 — studio watermark config. When non-null, every image
   *  gets composited on a canvas before it lands in the zip so
   *  the mark is baked into the pixels (JPEG re-encoded at 0.92).
   *  Null → identical to pre-Wave 6 (raw bytes preserved). */
  watermark?: WatermarkConfig | null;
}

// HEIC (iPhone) conversion. Lazy-imported so the ~1MB heic2any
// chunk only ships when a HEIC file is actually encountered.
const convertHeicToJpeg = async (blob: Blob): Promise<Blob> => {
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({
    blob,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  return Array.isArray(converted) ? converted[0] : converted;
};

// P1-2 — part-rotation ceiling. 500 MB of stored entries per ZIP:
// large enough that a typical prepped gallery (~1–3 MB/photo) still
// arrives as ONE file, small enough that peak memory (entries + the
// STORE-mode output blob) stays near ~1 GB even on a 500-photo
// full-res batch — inside what mobile Safari/Chrome tabs survive.
const PART_LIMIT_BYTES = 500 * 1024 * 1024;

export async function downloadZip({
  photos,
  filename = 'photos.zip',
  concurrency = 6,
  onProgress,
  watermark = null,
}: DownloadZipOptions): Promise<ZipResult> {
  if (photos.length === 0) {
    return { succeeded: 0, failed: 0, parts: 0, heicFallbacks: 0, watermarkFallbacks: 0 };
  }

  // Dynamic import keeps JSZip out of the main bundle.
  const JSZip = (await import('jszip')).default;
  // P1-2 — `zip` is now the CURRENT PART, replaced after each
  // rotation, so finished parts (and their blobs) become garbage the
  // moment they're saved instead of pinning the whole gallery.
  let zip = new JSZip();

  // Wave 6 — pre-load the watermark PNG ONCE for the whole batch.
  // Loading it per-photo would burn a network round-trip per zip
  // entry (50–500× per batch). The HTMLImageElement is passed into
  // every compositeWatermark call so drawImage is a pure paint,
  // not a fetch.
  const watermarkImage = watermark?.image_url
    ? await preloadImage(watermark.image_url).catch((err) => {
        // A missing / CORS-broken watermark image should NOT abort
        // the batch — degrade to text-only or no-op instead.
        console.error('[downloadZip] watermark image preload failed:', err);
        return null;
      })
    : null;

  const total = photos.length;
  let completed = 0;
  let succeeded = 0;
  let failed = 0;
  let cursor = 0;
  // P1-2 / DEMO-2 — degraded-but-included counters (see ZipResult).
  let heicFallbacks = 0;
  let watermarkFallbacks = 0;

  // ── P1-2 · Part state ─────────────────────────────────────────────
  //
  // Entries are appended through `appendChain`, a promise chain that
  // serializes "add entry / maybe rotate": JSZip mutation is cheap,
  // but a rotation runs generateAsync mid-batch and no worker may
  // append to the part being finalized. Workers keep FETCHING while a
  // part finalizes (their fetches aren't chained), so the network
  // never idles — at most `concurrency` extra blobs are in flight.
  let partBytes = 0;
  let partEntries = 0;
  let partsSaved = 0;
  // Flips true the first time a rotation is needed — from then on
  // every part (including the one being closed) gets `-partN` naming.
  // A gallery that never rotates keeps the caller's exact filename.
  let rotated = false;
  const baseName = filename.replace(/\.zip$/i, '') || 'photos';
  let appendChain: Promise<void> = Promise.resolve();

  // DL-SAFARI-1 — was a hand-rolled anchor that revoked the object URL
  // in the same tick as the click. Safari queues the download and reads
  // the URL afterwards, so it got a dead URL and silently saved
  // nothing; Chrome reads synchronously and was unaffected. See
  // `saveBlob`, which is now the only place this is written.
  const triggerBrowserDownload = (blob: Blob, name: string) => saveBlob(blob, name);

  // Finalize the current part and hand it to the browser. No-ops on
  // an empty part — which also means an all-failed run no longer
  // saves a zero-entry archive. (Chrome asks the user to allow
  // multiple automatic downloads the first time a second part saves;
  // callers surface `parts > 1` so that prompt isn't a mystery.)
  const savePart = async () => {
    if (partEntries === 0) return;
    // GAL-7 — STORE, not DEFLATE. JPEGs are already entropy-coded, so
    // DEFLATE saves ~0% while burning single-threaded CPU for MINUTES
    // on a multi-GB wedding gallery ("downloads take forever" was
    // mostly this zipping phase, not the network). STORE still writes
    // per-entry CRC-32 checksums, so integrity checking is unchanged.
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE',
    });
    const outName = rotated ? `${baseName}-part${partsSaved + 1}.zip` : filename;
    triggerBrowserDownload(blob, outName);
    partsSaved += 1;
    // Fresh part — the finalized JSZip (and every blob it referenced)
    // is now unreachable and collectable.
    zip = new JSZip();
    partBytes = 0;
    partEntries = 0;
  };

  const appendEntry = (name: string, blob: Blob): Promise<void> => {
    appendChain = appendChain.then(async () => {
      if (partEntries > 0 && partBytes + blob.size > PART_LIMIT_BYTES) {
        rotated = true;
        onProgress?.({ current: completed, total, phase: 'zipping' });
        await savePart();
        onProgress?.({ current: completed, total, phase: 'downloading' });
      }
      zip.file(name, blob);
      partBytes += blob.size;
      partEntries += 1;
    });
    return appendChain;
  };

  onProgress?.({ current: 0, total, phase: 'downloading' });

  const worker = async () => {
    while (cursor < total) {
      const idx = cursor++;
      const photo = photos[idx];
      try {
        // Hit the untouched original — the grid/lightbox render
        // preview variants, but downloads must be full-res.
        const res = await fetch(originalUrl(photo.file_url));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        let blob = await res.blob();
        let outName = photo.name;
        if (/\.heic$/i.test(photo.name)) {
          try {
            blob = await convertHeicToJpeg(blob);
            outName = photo.name.replace(/\.heic$/i, '.jpg');
          } catch (err) {
            console.error('[downloadZip] HEIC conversion failed for', photo.name, err);
            // Fall through and add the original HEIC bytes anyway —
            // but COUNTED now (DEMO-2), so the caller can say so.
            heicFallbacks += 1;
          }
        }
        // Wave 6 — canvas composite. Skips gracefully on failure
        // (SecurityError on tainted canvas, OOM on huge images)
        // and writes the pre-composite bytes as a fallback.
        if (watermark) {
          try {
            blob = await compositeWatermark(blob, watermark, watermarkImage);
            outName = outName.replace(/\.(png|webp|heic)$/i, '.jpg');
            if (!/\.(jpe?g)$/i.test(outName)) outName = `${outName}.jpg`;
          } catch (err) {
            console.error('[downloadZip] watermark bake failed for', photo.name, err);
            // DEMO-2 — degraded (unmarked bytes shipped), counted.
            watermarkFallbacks += 1;
          }
        }
        // Disambiguate duplicate filenames by prefixing an index;
        // JSZip silently overwrites entries with the same key, and
        // gallery photos frequently share names across shoots. The
        // global index also keeps names unique ACROSS parts.
        const safeName = `${String(idx + 1).padStart(3, '0')}_${outName}`;
        await appendEntry(safeName, blob);
        succeeded += 1;
      } catch (err) {
        console.error('[downloadZip] failed to fetch', photo.name, err);
        failed += 1;
      }
      completed += 1;
      onProgress?.({ current: completed, total, phase: 'downloading' });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, total) }, () => worker()),
  );
  // Every worker awaited its append, but drain the chain anyway so a
  // future refactor can't reorder the final flush ahead of an append.
  // Swallow a chain rejection here: an append/rotation failure was
  // already counted against its photo by the worker that awaited it,
  // and re-throwing it would discard the honest succeeded/failed
  // tally. The final savePart below still throws on a genuine
  // archive-build failure, which is the error the caller should see.
  await appendChain.catch(() => undefined);

  onProgress?.({ current: total, total, phase: 'zipping' });
  await savePart();

  onProgress?.({ current: total, total, phase: 'done' });

  return { succeeded, failed, parts: partsSaved, heicFallbacks, watermarkFallbacks };
}

// ─── Watermark canvas composite ─────────────────────────────────────
//
// Loads the source image into an off-screen canvas, draws the studio
// mark on top at the configured position/opacity/scale, and returns
// a fresh JPEG blob (quality 0.92 — matches HEIC path so the whole
// zip stays consistent).
//
// Called from both the bulk zip path (once per photo, watermark
// HTMLImageElement pre-loaded) and the single-photo download path in
// ClientGalleryView / PublicDelivery (dynamic-imports this file).
//
// Memory note: a 12MP DSLR photo (6000×4000) allocates ~96MB in the
// canvas backing store (4 bytes/pixel × 24M pixels). Chrome caps
// per-canvas memory at ~4096×4096 on some GPU-poor devices — larger
// sources will silently downscale. That's acceptable for a
// watermarked deliverable (the mark discourages redistribution;
// pixel-perfect resolution is a separate purchase).
//
// We tear down the canvas after every composite by nulling the
// context + reassigning width to 1 so GC can reclaim promptly.
// Otherwise a batch of 500 photos would keep several GB live.
export async function compositeWatermark(
  sourceBlob: Blob,
  config: WatermarkConfig,
  preloadedMarkImage?: HTMLImageElement | null,
): Promise<Blob> {
  const bitmap = await blobToImageBitmap(sourceBlob);
  const w = bitmap.width;
  const h = bitmap.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    throw new Error('canvas 2d unavailable');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();

  // Watermark image. Reuse the preloaded one if we got it from the
  // batch caller; otherwise load it now (single-photo path).
  let markImage: HTMLImageElement | null = preloadedMarkImage ?? null;
  if (!markImage && config.image_url) {
    try {
      markImage = await preloadImage(config.image_url);
    } catch (err) {
      console.error('[compositeWatermark] mark image load failed:', err);
      markImage = null;
    }
  }

  // Sizing — scale × the SHORTER edge of the source, capped so we
  // never render at less than a legible ~24px on tiny thumbnails.
  const minSide = Math.min(w, h);
  const markSize = Math.max(24, Math.round(minSide * config.scale));

  ctx.save();
  ctx.globalAlpha = config.opacity;

  if (config.position === 'tile' && markImage) {
    // Repeating tile — createPattern is cheap once, then a single
    // fillRect stamps every tile. Sized to markSize square.
    const tileCanvas = document.createElement('canvas');
    tileCanvas.width = markSize;
    tileCanvas.height = markSize;
    const tileCtx = tileCanvas.getContext('2d');
    if (tileCtx) {
      const iw = markImage.naturalWidth || markImage.width;
      const ih = markImage.naturalHeight || markImage.height;
      const scale = Math.min(markSize / iw, markSize / ih);
      const drawW = iw * scale;
      const drawH = ih * scale;
      tileCtx.drawImage(
        markImage,
        (markSize - drawW) / 2,
        (markSize - drawH) / 2,
        drawW,
        drawH,
      );
      const pattern = ctx.createPattern(tileCanvas, 'repeat');
      if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, w, h);
      }
    }
  } else {
    // Corner / center — draw the mark image (if any), then the text
    // (if any). Padding of 3% of the shorter edge matches the
    // overlay's inset visual weight.
    const pad = Math.round(minSide * 0.03);
    if (markImage) {
      const iw = markImage.naturalWidth || markImage.width;
      const ih = markImage.naturalHeight || markImage.height;
      const scale = Math.min(markSize / iw, markSize / ih);
      const drawW = iw * scale;
      const drawH = ih * scale;
      const [x, y] = anchorPixel(config.position, w, h, drawW, drawH, pad);
      ctx.drawImage(markImage, x, y, drawW, drawH);
    }
    if (config.text) {
      const fontSize = Math.round(markSize * 0.28);
      ctx.font = `600 ${fontSize}px system-ui, -apple-system, sans-serif`;
      ctx.fillStyle = '#ffffff';
      // Soft shadow for legibility on high-key backgrounds.
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = Math.max(2, Math.round(fontSize * 0.15));
      const metrics = ctx.measureText(config.text);
      const textW = metrics.width;
      const textH = fontSize;
      const [x, y] = anchorPixel(config.position, w, h, textW, textH, pad);
      // Baseline is textBottom; we want top-left, so shift y down
      // by textH.
      ctx.fillText(config.text, x, y + textH);
    }
  }

  ctx.restore();

  const out = await canvasToBlob(canvas);
  // Aggressive teardown — 500-photo batches otherwise pin GB of
  // canvas backing store until GC gets around to it.
  canvas.width = 1;
  canvas.height = 1;
  return out;
}

/// Pre-load an image URL with crossOrigin=anonymous so the resulting
/// canvas draw is NOT taint-locked (and toBlob works). Rejects when
/// the fetch or decode fails. Callers should degrade to no-op.
export function preloadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err instanceof Event ? new Error('image load failed') : err);
    img.src = url;
  });
}

/// Prefer createImageBitmap (async, off-main-thread decode) — falls
/// back to Image + object URL where unsupported (Safari <15).
async function blobToImageBitmap(blob: Blob): Promise<CanvasImageSource & { width: number; height: number; close?: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      // Fall through to the <img> path.
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    return img as unknown as CanvasImageSource & { width: number; height: number; close?: () => void };
  } finally {
    // Delay revoke — the caller draws from `img.src` synchronously
    // after this returns, so revoking immediately would race.
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas toBlob returned null'))),
      'image/jpeg',
      0.92,
    );
  });
}

function anchorPixel(
  position: WatermarkConfig['position'],
  w: number,
  h: number,
  markW: number,
  markH: number,
  pad: number,
): [number, number] {
  switch (position) {
    case 'bottom-right':
      return [w - markW - pad, h - markH - pad];
    case 'bottom-left':
      return [pad, h - markH - pad];
    case 'top-right':
      return [w - markW - pad, pad];
    case 'top-left':
      return [pad, pad];
    case 'center':
      return [(w - markW) / 2, (h - markH) / 2];
    default:
      return [w - markW - pad, h - markH - pad];
  }
}
